import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ApiClientError,
  idempotencyKeyFor,
  TransportError,
  type ApiError as ApiErrorPayload,
  type ApiMeta,
} from '@/lib/http-core';
import { adminFetch, apiClient } from '@/lib/apiClient';
import { uploadToS3 } from '@/lib/upload';
import { artistQueryKey } from './useArtistQuery';

type ArtistImageType = 'PROFILE' | 'BANNER' | 'GALLERY';

interface ErrorEnvelopeShape {
  success: false;
  error: ApiErrorPayload;
  meta?: ApiMeta;
}

const throwIfError = (raw: unknown, status: number, op: string): never => {
  const env = raw as ErrorEnvelopeShape | null | undefined;
  if (env && env.success === false && env.error) {
    throw new ApiClientError(env.error, { status, ...(env.meta ? { meta: env.meta } : {}) });
  }
  throw new TransportError(`Artist image ${op} failed with status ${status}`, raw);
};

interface PresignItem {
  imageId: string;
  objectKey: string;
  uploadUrl: string;
  uploadMethod: string;
  requiredHeaders: Record<string, string>;
  publicUrl: string;
}

interface ConfirmItem {
  imageId: string;
  type: ArtistImageType;
  url: string;
  status: string;
}

const postJson = async <T>(path: string, body: unknown, op: string): Promise<T> => {
  const response = await adminFetch(path, {
    method: 'POST',
    headers: {
      'Idempotency-Key': idempotencyKeyFor('POST', path, body),
    },
    body: JSON.stringify(body),
  });
  const parsed = (await response.json().catch(() => null)) as
    | { success?: boolean; data?: unknown; error?: ApiErrorPayload }
    | null;
  if (!parsed || parsed.success !== true) throwIfError(parsed, response.status, op);
  return (parsed as { data: T }).data;
};

const presignPath = (artistId: string) => `/admin/v1/artists/${artistId}/images/presign`;
const confirmPath = (artistId: string) => `/admin/v1/artists/${artistId}/images/confirm`;

export interface UploadArtistImageInput {
  artistId: string;
  type: ArtistImageType;
  file: File;
  sortOrder?: number;
  altText?: string;
}

/**
 * presign → S3 PUT(직행, Authorization 미포함) → confirm 3단계.
 * multipart 업로드를 대체해 415/413 를 회피한다(be-partner 계약).
 */
export const useUploadArtistImageMutation = () => {
  const qc = useQueryClient();
  return useMutation<ConfirmItem, Error, UploadArtistImageInput>({
    mutationFn: async ({ artistId, type, file, sortOrder, altText }) => {
      const presignFile = {
        filename: file.name,
        contentType: file.type,
        sizeBytes: file.size,
        imageType: type,
        ...(sortOrder !== undefined ? { sortOrder } : {}),
        ...(altText ? { altText } : {}),
      };
      const presign = await postJson<{ items: PresignItem[] }>(
        presignPath(artistId),
        { files: [presignFile] },
        'presign',
      );
      const slot = presign.items[0];
      if (!slot) throw new TransportError('presign returned no upload slot', presign);

      // QA-379 — S3 presigned URL 은 공용 업로더(lib/upload uploadToS3, 인증 헤더 없이 requiredHeaders 만)로 보낸다.
      // 종전엔 adminFetch 라 관리자 Authorization 헤더(+credentials)가 함께 나가 S3 가 400 으로 거부했다.
      await uploadToS3([
        {
          item: {
            fileId: slot.imageId,
            objectKey: slot.objectKey,
            uploadUrl: slot.uploadUrl,
            uploadMethod: 'PUT',
            requiredHeaders: slot.requiredHeaders ?? { 'Content-Type': file.type },
            publicUrl: slot.publicUrl,
          },
          file,
        },
      ]);

      const confirm = await postJson<{ items: ConfirmItem[] }>(
        confirmPath(artistId),
        { imageIds: [slot.imageId] },
        'confirm',
      );
      const confirmed = confirm.items.find((i) => i.imageId === slot.imageId) ?? confirm.items[0];
      if (!confirmed) throw new TransportError('confirm returned no image', confirm);
      return confirmed;
    },
    onSuccess: (_res, { artistId }) => {
      void qc.invalidateQueries({ queryKey: artistQueryKey(artistId) });
    },
  });
};

export interface DeleteArtistImageInput {
  artistId: string;
  imageId: string;
}

export const useDeleteArtistImageMutation = () => {
  const qc = useQueryClient();
  return useMutation<void, Error, DeleteArtistImageInput>({
    mutationFn: async ({ artistId, imageId }) => {
      const path = '/admin/v1/artists/{artistId}/images/{imageId}' as const;
      const concretePath = path
        .replace('{artistId}', artistId)
        .replace('{imageId}', imageId);
      const idempotencyKey = idempotencyKeyFor('DELETE', concretePath, '');
      const { error, response } = await apiClient.raw.DELETE(path, {
        params: {
          path: { artistId, imageId },
          header: { 'Idempotency-Key': idempotencyKey },
        },
      });
      if (response.status >= 400) throwIfError(error, response.status, 'delete');
    },
    onSuccess: (_void, { artistId }) => {
      void qc.invalidateQueries({ queryKey: artistQueryKey(artistId) });
    },
  });
};
